import { useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { GET_PLANS_BY_IDS_QUERY } from "../../queries/digitalTwinPlansQueries";
import {
  addPlan,
  copyPlan as copyPlanService,
} from "../../services/PlanService";

const MAX_PLANS_PER_TWIN = 3;

export const usePlans = (plansIds = [], digitalTwinId = "") => {
  const [isAddingPlan, setIsAddingPlan] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_PLANS_BY_IDS_QUERY, {
    variables: { ids: plansIds },
    skip: !plansIds?.length || !digitalTwinId,
    fetchPolicy: "network-only",
  });

  const plans = useMemo(() => {
    // Early return if we're intentionally skipping the query
    if (!digitalTwinId || !plansIds?.length) {
      return [];
    }

    // Safe check for data and the nested property
    if (data?.digitalTwinPlansByIds) {
      return data.digitalTwinPlansByIds;
    }

    // Only log error if we expected to get data but didn't
    if (!loading && data !== undefined) {
    }

    return [];
  }, [data, loading, digitalTwinId, plansIds?.length]);

  const createPlan = useCallback(async () => {
    if (isAddingPlan || !digitalTwinId || plans.length >= MAX_PLANS_PER_TWIN) {
      return null;
    }

    setIsAddingPlan(true);

    try {
      const planId = await addPlan({
        digital_twin_id: digitalTwinId,
        creation: new Date(),
      });

      return planId;
    } catch (error) {
      return null;
    } finally {
      setIsAddingPlan(false);
    }
  }, [isAddingPlan, digitalTwinId]);

  const duplicatePlan = useCallback(
    async plan => {
      if (
        isAddingPlan ||
        !digitalTwinId ||
        !plan?._id ||
        plans.length >= MAX_PLANS_PER_TWIN
      ) {
        return null;
      }

      setIsAddingPlan(true);

      try {
        const planId = await copyPlanService(plan._id, digitalTwinId);
        return planId;
      } catch (error) {
        return null;
      } finally {
        setIsAddingPlan(false);
      }
    },
    [isAddingPlan, digitalTwinId, plans.length]
  );

  return {
    plans,
    loading,
    error,
    isAddingPlan,
    createPlan,
    duplicatePlan,
    refetchPlans: refetch,
  };
};
